import { memo } from 'react';
import { Database, Gauge, Radio, ServerCog, ShieldCheck } from 'lucide-react';
import { Handle, Position, type Node, type NodeProps } from '@xyflow/react';
import type { StageNodeData, SystemNodeData } from '../lib/graph';
import { tr } from '../i18n';

export const StageLane = memo(({ data }: NodeProps<Node<StageNodeData>>) => (
  <section className="stage-lane" style={{ '--stage-tone': data.tone } as React.CSSProperties} aria-label={data.title}>
    <div className="stage-lane__header">
      {data.number && <span className="stage-number">{data.number}</span>}
      <div><span className="stage-code">{data.shortTitle}</span><h2>{data.title}</h2></div>
    </div>
    <div className="stage-lane__grid" />
  </section>
));
StageLane.displayName = 'StageLane';

const confidenceLabel = (data: SystemNodeData) => {
  const key = data.content.confidence === 'publicly-referenced' ? 'publiclyReferenced' : data.content.confidence === 'optional-example' ? 'optionalExample' : 'recommended';
  return tr(data.language, key);
};

export const SystemNode = memo(({ data }: NodeProps<Node<SystemNodeData>>) => {
  const { content } = data;
  const Icon = content.variant === 'hub' ? Database : content.variant === 'instrument' ? Gauge : content.variant === 'governance' ? ShieldCheck : content.stageId === 'activation' ? Radio : ServerCog;
  const classes = [
    'system-node', `system-node--${content.variant ?? 'system'}`, `confidence-${content.confidence}`, `role-${content.digitalMarketingRole}`,
    data.active ? 'is-active' : '', data.selected ? 'is-selected' : '', data.connected ? 'is-connected' : '', data.dimmed ? 'is-dimmed' : '', !data.funnelMatch ? 'is-funnel-muted' : '', data.roleLens ? 'role-lens-on' : '',
  ].filter(Boolean).join(' ');
  return (
    <article className={classes} tabIndex={0} aria-label={`${data.title}. ${confidenceLabel(data)}.`} data-testid={`node-${content.id}`}>
      <Handle type="target" position={Position.Left} id="left-target" />
      <Handle type="source" position={Position.Right} id="right-source" />
      <Handle type="target" position={Position.Top} id="top-target" />
      <Handle type="source" position={Position.Bottom} id="bottom-source" />
      <span className="node-icon"><Icon size={content.variant === 'hub' ? 23 : 15} strokeWidth={1.7} /></span>
      <span className="node-copy"><strong>{data.title}</strong><small>{confidenceLabel(data)}</small></span>
      <span className="funnel-badges" aria-label={`Funnel: ${content.funnelStages.join(', ')}`}>
        {content.funnelStages.map((stage) => <i key={stage} className={`funnel-badge funnel-badge--${stage}`}>{stage.toUpperCase()}</i>)}
      </span>
      <span className={`confidence-dot confidence-dot--${content.confidence}`} aria-hidden="true" />
      <span className="node-tooltip" role="tooltip"><strong>{data.title}</strong>{content.summary[data.language]}<em>{confidenceLabel(data)}</em></span>
    </article>
  );
});
SystemNode.displayName = 'SystemNode';
